/**
 * Flow-scoped, liveness-guarded, coalescing resync loop — the scheduling shell
 * around sync.resync, pure so it is testable without the hook's providers.
 *
 * `resync` fetches async, so every await is followed by a scope/liveness check
 * (isCurrent): a flow switch (A→B) or unmount mid-fetch must not hydrate A's
 * snapshot into B or reconcile A's workflow onto B's canvas. A trigger that
 * arrives while a pass is in flight — a second stream_gap, a flow switch, or a
 * terminal true-up — sets `pending`; the loop services it once the current
 * pass finishes (re-reading getPhloId, so it picks up the new flow). This is
 * what fixes both the swallowed second gap and the lost B-mount resync without
 * resetting the shared re-entrancy guard mid-flight (a swallowed gap leaves
 * the run permanently detached mid-build).
 */

export interface ResyncSchedulerDeps {
  getPhloId: () => string | null
  isAlive: () => boolean
  resync: (
    phloId: string,
    isCurrent: () => boolean,
  ) => Promise<{ workflow?: string | null; run_status?: string | null } | null>
  /** Delete-safe canvas reconcile against the authoritative snapshot — the
   *  canvas may have diverged in both directions while detached. */
  reconcile: (workflow: string) => Promise<void>
  /** Interrupted run with NO persisted draft (died pre-first-snapshot): the
   *  canvas may carry orphan nodes from the dead run's live events. True it up
   *  from the last SAVED flow (flowData reseed) so a resume never serializes
   *  the orphans — the duplicate-tree bug. */
  onStaleInterrupted: () => void
  /** Snapshot reported a LIVE run (running/waiting_input). Mount rehydration
   *  uses this to auto-open the panel once per flow — a build running in the
   *  background must be visible when the user lands on the page. */
  onLiveRun?: () => void
}

export const createResyncScheduler = ({
  getPhloId,
  isAlive,
  resync,
  reconcile,
  onStaleInterrupted,
  onLiveRun,
}: ResyncSchedulerDeps): { trigger: () => Promise<void> } => {
  let running = false
  let pending = false

  const trigger = async (): Promise<void> => {
    if (!getPhloId()) return
    if (running) {
      pending = true
      return
    }
    running = true
    try {
      do {
        pending = false
        const capturedId = getPhloId()
        if (!capturedId || !isAlive()) break
        const isCurrent = () => isAlive() && getPhloId() === capturedId
        const snapshot = await resync(capturedId, isCurrent)
        // Scope/liveness changed during the fetch — skip the canvas write and
        // let the loop re-evaluate (it re-runs for the new flow if pending).
        if (!isCurrent()) continue
        if (snapshot?.workflow) await reconcile(snapshot.workflow)
        else if (snapshot?.run_status === 'interrupted') onStaleInterrupted()
        if (snapshot?.run_status === 'running' || snapshot?.run_status === 'waiting_input') {
          onLiveRun?.()
        }
      } while (pending)
    } finally {
      running = false
    }
  }

  return { trigger }
}
